import { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "@emotion/react";
import { Box, Button, Stack } from "@mui/material";
import AmyAvatar from "./AmyAvatar";

const LAUNCHER_ICON_SIZE = 132;
const EDGE_PADDING = 16;
const DRAG_THRESHOLD_PX = 6;
const POSITION_STORAGE_KEY = "amy-launcher-position";

function getDefaultPosition(width, height) {
  return {
    left: Math.max(EDGE_PADDING, window.innerWidth - width - EDGE_PADDING),
    top: Math.max(EDGE_PADDING, window.innerHeight - height - EDGE_PADDING),
  };
}

function clampPosition(left, top, width, height) {
  const maxLeft = Math.max(EDGE_PADDING, window.innerWidth - width - EDGE_PADDING);
  const maxTop = Math.max(EDGE_PADDING, window.innerHeight - height - EDGE_PADDING);

  return {
    left: Math.min(Math.max(EDGE_PADDING, left), maxLeft),
    top: Math.min(Math.max(EDGE_PADDING, top), maxTop),
  };
}

function readStoredPosition(width, height) {
  try {
    const raw = localStorage.getItem(POSITION_STORAGE_KEY);
    if (!raw) {
      return getDefaultPosition(width, height);
    }

    const parsed = JSON.parse(raw);
    if (typeof parsed?.left !== "number" || typeof parsed?.top !== "number") {
      return getDefaultPosition(width, height);
    }

    return clampPosition(parsed.left, parsed.top, width, height);
  } catch {
    return getDefaultPosition(width, height);
  }
}

function persistPosition(position) {
  try {
    localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position));
  } catch {
    // Ignore storage failures (private mode, quota, etc.).
  }
}

export default function AmyLauncherButton({ onClick }) {
  const { palette } = useTheme();
  const { white } = palette;
  const rootRef = useRef(null);
  const dragStateRef = useRef(null);
  const dragListenersRef = useRef(null);
  const suppressClickRef = useRef(false);
  const openedFromClickRef = useRef(false);
  const [pressed, setPressed] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [position, setPosition] = useState(() =>
    typeof window === "undefined"
      ? { left: EDGE_PADDING, top: EDGE_PADDING }
      : readStoredPosition(LAUNCHER_ICON_SIZE, LAUNCHER_ICON_SIZE + 48)
  );

  const measureAndClamp = useCallback((nextLeft, nextTop) => {
    const node = rootRef.current;
    const width = node?.offsetWidth || LAUNCHER_ICON_SIZE;
    const height = node?.offsetHeight || LAUNCHER_ICON_SIZE + 48;
    return clampPosition(nextLeft, nextTop, width, height);
  }, []);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) {
      return undefined;
    }

    const width = node.offsetWidth || LAUNCHER_ICON_SIZE;
    const height = node.offsetHeight || LAUNCHER_ICON_SIZE + 48;
    setPosition(readStoredPosition(width, height));

    const handleResize = () => {
      setPosition((current) => measureAndClamp(current.left, current.top));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [measureAndClamp]);

  const detachDragListeners = useCallback(() => {
    const listeners = dragListenersRef.current;
    if (!listeners) {
      return;
    }

    window.removeEventListener("pointermove", listeners.move);
    window.removeEventListener("pointerup", listeners.up);
    window.removeEventListener("pointercancel", listeners.up);
    dragListenersRef.current = null;
  }, []);

  useEffect(() => detachDragListeners, [detachDragListeners]);

  const handleOpen = (event) => {
    if (suppressClickRef.current || dragStateRef.current?.didDrag) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    openedFromClickRef.current = true;
    onClick?.();
  };

  const handlePointerDown = (event) => {
    if (event.button !== 0 && event.pointerType === "mouse") {
      return;
    }

    const node = rootRef.current;
    if (!node) {
      return;
    }

    const rect = node.getBoundingClientRect();
    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originLeft: rect.left,
      originTop: rect.top,
      didDrag: false,
    };

    setPressed(true);

    const handlePointerMove = (moveEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState || dragState.pointerId !== moveEvent.pointerId) {
        return;
      }

      const deltaX = moveEvent.clientX - dragState.startX;
      const deltaY = moveEvent.clientY - dragState.startY;
      const distance = Math.hypot(deltaX, deltaY);

      if (!dragState.didDrag && distance < DRAG_THRESHOLD_PX) {
        return;
      }

      if (!dragState.didDrag) {
        dragState.didDrag = true;
        setDragging(true);
        try {
          node.setPointerCapture?.(moveEvent.pointerId);
        } catch {
          // Capture can fail if the pointer is already gone. Window listeners still track the drag.
        }
      }

      moveEvent.preventDefault();
      const next = measureAndClamp(dragState.originLeft + deltaX, dragState.originTop + deltaY);
      setPosition(next);
    };

    const endDrag = (endEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState || dragState.pointerId !== endEvent.pointerId) {
        return;
      }

      detachDragListeners();
      try {
        node.releasePointerCapture?.(endEvent.pointerId);
      } catch {
        // Ignore if this pointer was never captured.
      }

      if (dragState.didDrag) {
        suppressClickRef.current = true;
        window.setTimeout(() => {
          suppressClickRef.current = false;
        }, 250);

        setPosition((current) => {
          const clamped = measureAndClamp(current.left, current.top);
          persistPosition(clamped);
          return clamped;
        });
      } else if (endEvent.type !== "pointercancel") {
        window.setTimeout(() => {
          if (openedFromClickRef.current) {
            openedFromClickRef.current = false;
            return;
          }
          onClick?.();
        }, 0);
      }

      setPressed(false);
      setDragging(false);
      dragStateRef.current = null;
    };

    detachDragListeners();
    dragListenersRef.current = { move: handlePointerMove, up: endDrag };
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);
  };

  return (
    <Stack
      ref={rootRef}
      alignItems="center"
      spacing={0.75}
      onPointerDown={handlePointerDown}
      sx={{
        position: "fixed",
        left: position.left,
        top: position.top,
        zIndex: 1400,
        width: LAUNCHER_ICON_SIZE,
        flexShrink: 0,
        touchAction: "none",
        userSelect: "none",
        cursor: dragging ? "grabbing" : "pointer",
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={handleOpen}
        aria-label="Open Amy assistant"
        onMouseDown={() => setPressed(true)}
        onMouseUp={() => setPressed(false)}
        onMouseLeave={() => {
          if (!dragging) {
            setPressed(false);
          }
        }}
        sx={{
          border: 0,
          p: 0,
          m: 0,
          width: "100%",
          cursor: "inherit",
          background: "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "& .amy-launcher-wrap": {
            display: "flex",
            transition: dragging ? "none" : "transform 0.28s cubic-bezier(0.34, 1.2, 0.64, 1)",
            willChange: "transform",
          },
          "&:hover .amy-launcher-wrap": {
            transform: dragging ? "none" : pressed ? "scale(0.96)" : "scale(1.08)",
          },
          "&:active .amy-launcher-wrap": {
            transform: dragging ? "none" : "scale(0.94)",
          },
          "&:focus-visible": {
            outline: "2px solid #3b82f6",
            outlineOffset: 4,
            borderRadius: "12px",
          },
        }}
      >
        <Box className="amy-launcher-wrap">
          <AmyAvatar size={LAUNCHER_ICON_SIZE} withBackground={false} animate={!dragging} />
        </Box>
      </Box>

      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={handleOpen}
        aria-label="Open Amy assistant"
        sx={{
          alignSelf: "center",
          transform: "translateX(-14px)",
          borderRadius: "999px",
          px: 2.25,
          py: 0.5,
          minHeight: 30,
          fontSize: "0.8125rem",
          fontWeight: 700,
          textTransform: "none",
          letterSpacing: "0.01em",
          color: white.main,
          cursor: "inherit",
          boxShadow: "0 4px 14px rgba(37, 99, 235, 0.35)",
          "&:hover": {
            color: white.main,
          },
          "&:focus": {
            color: white.main,
          },
        }}
      >
        Amy
      </Button>
    </Stack>
  );
}
